import { useEffect, useState } from "react";
import { AccessibilityInfo, Platform, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { Card } from "@/components/ui/Card";
import { Screen } from "@/components/ui/Screen";
import { Skeleton } from "@/components/ui/Skeleton";
import type { BrowseLayout } from "@/hooks/usePublicBrowseView";

// How long a visitor watches the placeholders before the status admits the wait. A large shop's
// first open on a cold server can take several seconds, and an unexplained wait reads as broken.
export const SLOW_LOAD_MS = 4000;

/**
 * Says in words what the placeholders show, and is the screen reader's loading announcement. After
 * `SLOW_LOAD_MS` it says the wait is expected, so a slow first open is not mistaken for a hang.
 */
const SLOW_COPY = "Still loading. A big collection can take a few seconds the first time.";

function LoadingStatus() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSlow(true);
      // A live region is Android's and the web's; VoiceOver hears a text change only when told.
      if (Platform.OS === "ios") AccessibilityInfo.announceForAccessibility(SLOW_COPY);
    }, SLOW_LOAD_MS);
    return () => clearTimeout(timer);
  }, []);
  return (
    <Text
      testID="public-browse-loading-status"
      accessibilityLiveRegion="polite"
      aria-live="polite"
      className="px-4 pb-3 text-center text-sm font-body text-muted-foreground"
    >
      {slow ? SLOW_COPY : "Loading cards…"}
    </Text>
  );
}

/**
 * The customer browse loading state (#465): placeholder cells shaped like the real ones under a
 * header shell.
 *
 * It fills the screen with the app's own ground, so the first frame is not a white flash. The
 * placeholders are `Skeleton`s, which pulse and are hidden from screen readers; a line of text under them says what is
 * loading, aloud as well, and admits a slow wait once it is one (`LoadingStatus`).
 *
 * `layout` matches the storefront being opened, so the hand-off does not shift the page or change
 * the cell shapes: `/inventory/[slug]` opens on a grid, and `"detail"` is the item page a label QR
 * opens, with a 44pt back bar over a hero, an identity and a price card.
 */
export function PublicBrowseSkeleton(
  { layout = "list" }: { layout?: BrowseLayout | "detail"; },
) {
  // Below the status bar, as each real public screen's header is; without it the placeholders
  // drew under the clock and the hand-off jumped by the inset.
  const { top } = useSafeAreaInsets();
  if (layout === "detail") {
    return (
      <Screen style={{ paddingTop: top }}>
        {/* The detail page's back bar: 12 + 44 + 12 above a hairline rule. */}
        <View className="flex-row items-center gap-2 border-b border-border px-3 py-3">
          <Skeleton className="rounded h-11 w-11 rounded-xl" />
          <Skeleton className="rounded h-5 flex-1" />
        </View>
        <View className="gap-4 px-4 pt-3">
          <View className="flex-row gap-3.5">
            <Skeleton className="rounded aspect-[3/4] w-[170px] rounded-xl" />
            <View className="flex-1 gap-2">
              <Skeleton className="rounded h-4 w-2/3" />
              <Skeleton className="rounded h-4 w-1/2" />
              <Skeleton className="rounded h-4 w-2/3" />
            </View>
          </View>
          <View className="gap-1.5">
            <Skeleton className="rounded h-5 w-3/4" />
            <Skeleton className="rounded h-4 w-2/5" />
          </View>
          <Card className="flex-row items-center justify-between">
            <Skeleton className="rounded h-4 w-24" />
            <Skeleton className="rounded h-6 w-20" />
          </Card>
        </View>
        <View className="pt-4">
          <LoadingStatus />
        </View>
      </Screen>
    );
  }
  return (
    <Screen style={{ paddingTop: top }}>
      {
        /* The real storefront's header: shop badge and name, the search box, and the sort/view
           row, each at its real height (60, 44, 44). */
      }
      <View className="gap-4 px-4 pb-4 pt-4">
        <View className="flex-row items-center gap-4">
          <Skeleton className="rounded h-[60px] w-[60px] rounded-xl" />
          <View className="min-w-0 flex-1 gap-2">
            <Skeleton className="rounded h-6 w-1/2" />
            <Skeleton className="rounded h-4 w-1/3" />
          </View>
        </View>
        <Skeleton className="rounded h-11 w-full" />
        <Skeleton className="rounded h-11 w-44" />
      </View>
      {/* Above the cells, so it is on screen at any height. */}
      <LoadingStatus />
      {layout === "grid"
        ? (
          <View className="flex-row flex-wrap gap-2 px-4">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <View key={i} className="min-w-[30%] flex-1 gap-2">
                <Skeleton className="rounded aspect-[3/4] w-full" />
                <Skeleton className="rounded h-4 w-4/5" />
              </View>
            ))}
          </View>
        )
        : (
          <View className="gap-2 px-4">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Card key={i} className="flex-row items-center gap-3">
                <Skeleton className="rounded h-16 w-12" />
                <View className="flex-1 gap-2">
                  <Skeleton className="rounded h-4 w-2/3" />
                  <Skeleton className="rounded h-3 w-16" />
                </View>
                <Skeleton className="rounded h-4 w-14" />
              </Card>
            ))}
          </View>
        )}
    </Screen>
  );
}
